import {ABILITY_CAST_TEXTURE_KEYS} from '../../configs/abilityCastAnimation';
import {
  BUILDING_IMPACT_COLORS as COLORS,
  BUILDING_IMPACT_FLIGHT,
  BUILDING_IMPACT_TEXTURE_KEYS,
  BUILDING_IMPACTS,
} from '../../configs/buildingConstructionAnimation';
import {BUILDING_DROP} from '../../configs/buildingDropAnimation';
import {CELL_OUTLINE_TEXTURE_KEYS} from '../../configs/fieldCellOutlines';
import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {SHOT_SHARDS_SHEET} from '../../configs/shotAnimation';
import {easeOutCubic, easeOutQuad, mixColor} from './buildAbilityCast';
import {BUILDING_IMPACT_TEXTURE_SIZES} from './buildingImpactTextures';
import {CELL_OUTLINE_RADIUS_PX, CELL_OUTLINE_TEXTURE_SIZE} from './cellOutlineTextures';

/**
 * ПЛАНЫ АНИМАЦИИ СТРОИТЕЛЬСТВА (см. configs/buildingConstructionAnimation): что и
 * когда рисуется, пока жетон летит, и в момент удара о землю. Здесь только расчет:
 * рисует план scenes/AbilityCastAnimation, формат — как у utils/effects/buildAbilityCast.
 *
 * Слой 'air' лежит под двойником падающего жетона, а 'ground' — над жетонами поля
 * и под отрядами: лучи и искры идут из-за жетона, а волны и трещины идут по земле.
 *
 * Случайность — только вид: на игру она не влияет.
 */

const TWO_PI = Math.PI * 2;
// Земля под углом: круги на ней чуть сплющены
const GROUND_SQUASH = 0.8;

const clamp01 = (value) => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
const lerp = (from, to, share) => from + (to - from) * share;
const randomRange = (rng, {min, max}) => lerp(min, max, rng());
const fadeOutFrom = (local, fromShare) => (local <= fromShare ? 1 : (1 - local) / (1 - fromShare));
const fadeInOut = (local, inShare, outShare) => {
  if (local < inShare) {
    return local / inShare;
  }
  return local > 1 - outShare ? (1 - local) / outShare : 1;
};

const createElement = ({
  textureKey, frame, tint = COLORS.white, sizePx, startMs = 0, durationMs, layer = 'air', order = 0, pose,
}) => ({textureKey, frame, tint, sizePx, startMs, endMs: startMs + durationMs, layer, order, pose});

const toPlan = (elements, {impactMs = 0, shake} = {}) => ({
  elements,
  totalMs: Math.max(0, ...elements.map((element) => element.endMs)),
  impactMs,
  ...(shake ? {shake} : {}),
});

// Размер картинки кольца, при котором его вершины лежат на радиусе hexSize
const resolveOutlineSizePx = (hexSize) => hexSize * CELL_OUTLINE_TEXTURE_SIZE / CELL_OUTLINE_RADIUS_PX;

// Падение с разгоном: тот же закон, что у твина двойника (Quadratic.In)
const resolveFallShare = (elapsedMs, flightMs) => clamp01(elapsedMs / flightMs) ** 2;

// Столб света от точки вверх: растягивается по высоте, основание стоит на месте
const beamPose = ({baseX, baseY, widthPx, heightPx, widthShare, alpha}) => {
  const {width, height} = BUILDING_IMPACT_TEXTURE_SIZES[BUILDING_IMPACT_TEXTURE_KEYS.BEAM];
  return {
    x: baseX,
    y: baseY - heightPx / 2,
    scaleX: widthShare,
    scaleY: heightPx / (widthPx * (height / width)),
    alpha,
  };
};

/**
 * Пока жетон летит: метка места падения, шлейф, призраки жетона; у основания города
 * столб света с неба и стягивающиеся к ячейке блики
 *
 * @param {Object} params
 * @param {{x: number, y: number}} params.center — центр ячейки
 * @param {number} params.hexSize
 * @param {string} params.kind — BUILDING_IMPACT_KINDS
 * @param {{accent: number, light: number}} params.palette
 * @param {?{textureKey: string, sizePx: number, shiftY: number}} [params.ghost] —
 *   картинка жетона в обычном размере
 * @param {function(): number} [params.rng]
 */
export const buildBuildingApproachCastPlan = ({center, hexSize, kind, palette, ghost, rng = Math.random}) => {
  const {flightMs, approach} = BUILDING_IMPACTS[kind];
  const size = hexSize;
  const {markFromScale, markSizeFactor, streakLengthFactor, ghostLagsMs} = BUILDING_IMPACT_FLIGHT;
  const dropHeight = size * BUILDING_DROP.heightFactor;
  const startScale = BUILDING_DROP.startScaleFactor;
  const elements = [];
  const tokenShiftY = ghost?.shiftY || 0;
  const tokenYAt = (share) => center.y + tokenShiftY - dropHeight * (1 - share);

  // Метка на земле: чем ближе жетон, тем она ярче и теснее к ячейке
  const markPose = (extraAlpha) => (local) => ({
    x: center.x,
    y: center.y,
    scale: lerp(markFromScale, 1, local * local),
    alpha: approach.markAlpha * extraAlpha * local ** 1.4,
  });
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: palette.accent, sizePx: size * markSizeFactor * 2,
    durationMs: flightMs, layer: 'ground', order: 0, pose: markPose(0.7),
  }));
  elements.push(createElement({
    textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint: palette.accent, sizePx: resolveOutlineSizePx(size),
    durationMs: flightMs, layer: 'ground', order: 1, pose: markPose(1),
  }));
  elements.push(createElement({
    textureKey: CELL_OUTLINE_TEXTURE_KEYS.CORE, tint: palette.light, sizePx: resolveOutlineSizePx(size),
    durationMs: flightMs, layer: 'ground', order: 2, pose: markPose(1),
  }));

  if (approach.beam) {
    const {widthFactor, heightFactor, alpha, fadeInShare} = approach.beam;
    const heightPx = size * heightFactor;
    [
      {tint: palette.accent, widthPx: size * widthFactor, strength: 1, order: 3},
      {tint: palette.light, widthPx: size * widthFactor * 0.42, strength: 0.9, order: 4},
    ].forEach(({tint, widthPx, strength, order}) => {
      elements.push(createElement({
        textureKey: BUILDING_IMPACT_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx,
        durationMs: flightMs, order,
        pose: (local) => beamPose({
          baseX: center.x,
          baseY: center.y + size * 0.3,
          widthPx,
          heightPx,
          widthShare: lerp(0.65, 1, easeOutQuad(local)),
          alpha: alpha * strength * clamp01(local / fadeInShare),
        }),
      }));
    });
  }

  if (approach.gather) {
    const {count, radiusFactor, sizeFactor, durationShare} = approach.gather;
    const lifeMs = flightMs * durationShare;
    for (let index = 0; index < count; index += 1) {
      const startAngle = (index + rng()) * TWO_PI / count;
      const radius = size * radiusFactor * lerp(0.7, 1, rng());
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: index % 2 === 0 ? palette.light : palette.accent,
        sizePx: size * randomRange(rng, sizeFactor) * 2,
        startMs: (flightMs - lifeMs) * rng(), durationMs: lifeMs, order: 5,
        pose: (local) => {
          const reach = 1 - local * local;
          const angle = startAngle + local * 1.5;
          return {
            x: center.x + Math.cos(angle) * radius * reach,
            y: center.y + Math.sin(angle) * radius * reach * GROUND_SQUASH,
            scale: lerp(1, 0.6, local),
            alpha: Math.min(1, local * 4),
          };
        },
      }));
    }
  }

  // Шлейф за жетоном: длиннее там, где жетон уже разогнался
  [
    {tint: palette.accent, widthShare: 1, strength: 1, order: 6},
    {tint: palette.light, widthShare: 0.42, strength: 1, order: 7},
  ].forEach(({tint, widthShare, strength, order}) => {
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint, sizePx: size,
      durationMs: flightMs, order,
      pose: (local) => {
        const share = local * local;
        const lengthPx = Math.max(1, size * streakLengthFactor * local);
        return {
          x: center.x,
          y: tokenYAt(share) - lengthPx / 2,
          scaleX: approach.streakWidthFactor * widthShare * lerp(startScale * 0.6, 1, share),
          scaleY: lengthPx / size,
          alpha: approach.streakAlpha * strength * clamp01(local / 0.3),
        };
      },
    }));
  });

  // Призраки жетона отстают от него: на глаз это скорость
  if (ghost) {
    ghostLagsMs.forEach((lagMs, index) => {
      elements.push(createElement({
        textureKey: ghost.textureKey, tint: palette.light, sizePx: ghost.sizePx,
        durationMs: flightMs, order: 8 + index,
        pose: (local, elapsedMs) => {
          const share = resolveFallShare(elapsedMs - lagMs, flightMs);
          return {
            x: center.x,
            y: tokenYAt(share),
            scale: lerp(startScale, 1, share),
            alpha: elapsedMs < lagMs ? 0 : approach.ghostAlpha * (1 - index * 0.28) * clamp01(share * 6),
          };
        },
      }));
    });
  }

  return toPlan(elements, {impactMs: flightMs});
};

// Волны идут по земле шестиугольником и вырастают из контура самой ячейки
const pushRings = (elements, {center, size, rings, palette}) => {
  const sizePx = resolveOutlineSizePx(size);
  rings.forEach(({startMs, durationMs, reachFactor, alpha}, index) => {
    const pose = (strength) => (local) => ({
      x: center.x,
      y: center.y,
      scale: lerp(0.8, reachFactor, easeOutCubic(local)),
      alpha: alpha * strength * (1 - local) ** 1.15,
    });
    elements.push(createElement({
      textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint: palette.accent, sizePx,
      startMs, durationMs, layer: 'ground', order: 30 + index * 2, pose: pose(1),
    }));
    elements.push(createElement({
      textureKey: index === 0 ? CELL_OUTLINE_TEXTURE_KEYS.CORE_THICK : CELL_OUTLINE_TEXTURE_KEYS.CORE,
      tint: palette.light, sizePx,
      startMs, durationMs, layer: 'ground', order: 31 + index * 2, pose: pose(1),
    }));
  });
};

// Подлет по дуге: вылетает у края жетона, дальше вдоль земли и чуть вверх
const resolveThrownPoint = ({center, angle, startRadius, reach, lift}, local) => {
  const share = easeOutCubic(local);
  const radius = lerp(startRadius, reach, share);
  return {
    x: center.x + Math.cos(angle) * radius,
    y: center.y + Math.sin(angle) * radius * GROUND_SQUASH - lift * Math.sin(local * Math.PI),
  };
};

// Искры вытянуты по ходу полета: так они читаются и на пыли, и на светлой подложке
const pushSparks = (elements, {center, size, sparks, palette, rng}) => {
  for (let index = 0; index < sparks.count; index += 1) {
    const throwParams = {
      center,
      angle: (index + rng()) * TWO_PI / sparks.count,
      startRadius: size * 0.35,
      reach: size * randomRange(rng, sparks.reachFactor),
      lift: size * lerp(0.2, 0.7, rng()),
    };
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: palette.light,
      sizePx: size * randomRange(rng, sparks.sizeFactor),
      startMs: 30 * rng(), durationMs: randomRange(rng, sparks.lifeMs), order: 40,
      pose: (local) => {
        const point = resolveThrownPoint(throwParams, local);
        const ahead = resolveThrownPoint(throwParams, Math.min(1, local + 0.02));
        return {
          ...point,
          rotation: Math.atan2(ahead.y - point.y, ahead.x - point.x),
          scaleX: lerp(3.4, 1, easeOutQuad(local)),
          scaleY: lerp(0.8, 0.5, local),
          alpha: fadeOutFrom(local, 0.55),
          tint: mixColor(palette.light, palette.accent, local * 1.8),
        };
      },
    }));
  }
};

const pushShards = (elements, {center, size, shards, rng}) => {
  const {frameCount, bigFrameCount, key} = SHOT_SHARDS_SHEET;
  for (let index = 0; index < shards.count; index += 1) {
    const isBig = index % 3 === 0;
    const [firstFrame, frames] = isBig ? [0, bigFrameCount] : [bigFrameCount, frameCount - bigFrameCount];
    const throwParams = {
      center,
      angle: (index + rng() * 0.8) * TWO_PI / shards.count,
      startRadius: size * 0.3,
      reach: size * randomRange(rng, shards.reachFactor) * (isBig ? 0.8 : 1),
      lift: size * lerp(0.35, 0.95, rng()),
    };
    const spin = (rng() < 0.5 ? -1 : 1) * lerp(1, 2.6, rng()) * TWO_PI;
    const startAngle = rng() * TWO_PI;
    elements.push(createElement({
      textureKey: key, frame: firstFrame + Math.floor(rng() * frames * 0.999), tint: COLORS.white,
      sizePx: size * randomRange(rng, shards.sizeFactor) * (isBig ? 1.25 : 1),
      durationMs: randomRange(rng, shards.lifeMs), order: 41,
      pose: (local) => ({
        ...resolveThrownPoint(throwParams, local),
        rotation: startAngle + spin * local,
        scale: lerp(1, 0.75, local),
        alpha: fadeOutFrom(local, 0.72),
      }),
    }));
  }
};

const pushRays = (elements, {center, size, rays, palette, rng}) => {
  [
    {tint: palette.accent, sizePx: size * rays.sizeFactor, direction: 1, strength: 1, order: 10},
    {tint: palette.light, sizePx: size * rays.sizeFactor * 0.68, direction: -1, strength: 0.9, order: 11},
  ].forEach(({tint, sizePx, direction, strength, order}) => {
    const startAngle = rng() * TWO_PI;
    elements.push(createElement({
      textureKey: BUILDING_IMPACT_TEXTURE_KEYS.RAYS, tint, sizePx,
      durationMs: rays.durationMs, order,
      pose: (local) => ({
        x: center.x,
        y: center.y,
        scale: lerp(0.45, 1, easeOutCubic(clamp01(local * 3))),
        rotation: startAngle + direction * rays.turns * TWO_PI * local,
        alpha: rays.alpha * strength * fadeInOut(local, 0.05, 0.6),
      }),
    }));
  });
};

// Столб света выстреливает вверх из ячейки и истончается
const pushPillar = (elements, {center, size, pillar, palette}) => {
  const heightPx = size * pillar.heightFactor;
  [
    {tint: palette.accent, widthPx: size * pillar.widthFactor, strength: 1, order: 12},
    {tint: palette.light, widthPx: size * pillar.widthFactor * 0.4, strength: 1, order: 13},
  ].forEach(({tint, widthPx, strength, order}) => {
    elements.push(createElement({
      textureKey: BUILDING_IMPACT_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx,
      durationMs: pillar.durationMs, order,
      pose: (local) => {
        const grow = easeOutCubic(clamp01(local / 0.16));
        return beamPose({
          baseX: center.x,
          baseY: center.y + size * 0.3,
          widthPx,
          heightPx: Math.max(1, heightPx * grow),
          widthShare: local < 0.16 ? lerp(0.5, 1.25, grow) : lerp(1.25, 0.05, easeOutQuad((local - 0.16) / 0.84)),
          alpha: pillar.alpha * strength * fadeOutFrom(local, 0.42),
        });
      },
    }));
  });
};

const pushMotes = (elements, {center, size, motes, palette, rng}) => {
  const tints = [palette.light, palette.accent, COLORS.crackHot];
  for (let index = 0; index < motes.count; index += 1) {
    const angle = (index + rng()) * TWO_PI / motes.count;
    const startRadius = size * motes.spreadFactor * Math.sqrt(rng());
    const rise = size * randomRange(rng, motes.riseFactor);
    const swayPhase = rng() * TWO_PI;
    const startX = center.x + Math.cos(angle) * startRadius;
    const startY = center.y + Math.sin(angle) * startRadius * GROUND_SQUASH;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: tints[index % tints.length],
      sizePx: size * randomRange(rng, motes.sizeFactor) * 2,
      startMs: randomRange(rng, motes.delayMs), durationMs: randomRange(rng, motes.lifeMs), order: 42,
      pose: (local) => ({
        x: startX + Math.sin(local * TWO_PI * 1.1 + swayPhase) * size * 0.13,
        y: startY - rise * easeOutQuad(local),
        scale: lerp(1, 0.5, local),
        alpha: fadeInOut(local, 0.14, 0.55),
      }),
    }));
  }
};

// Печать на ячейке основанного города: контур цвета игрока пульсирует и гаснет
const pushSeal = (elements, {center, size, seal, palette}) => {
  const sizePx = resolveOutlineSizePx(size);
  [
    {textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint: palette.accent, order: 20},
    {textureKey: CELL_OUTLINE_TEXTURE_KEYS.CORE_THICK, tint: palette.light, order: 21},
  ].forEach(({textureKey, tint, order}) => {
    elements.push(createElement({
      textureKey, tint, sizePx,
      durationMs: seal.durationMs, layer: 'ground', order,
      pose: (local, elapsedMs) => ({
        x: center.x,
        y: center.y,
        scale: 1,
        alpha: seal.alpha * fadeOutFrom(local, 0.55) * (0.72 + 0.28 * Math.cos(elapsedMs * TWO_PI / seal.pulseMs)),
      }),
    }));
  });
};

/**
 * Удар жетона о землю. Первый кадр плана — момент касания
 *
 * @param {Object} params
 * @param {{x: number, y: number}} params.center — центр ячейки
 * @param {number} params.hexSize
 * @param {string} params.kind — BUILDING_IMPACT_KINDS
 * @param {{accent: number, light: number}} params.palette
 * @param {boolean} [params.isReduced] — на поле уже идет несколько ударов (пачка из
 *   плана): без осколков, искр, лучей и бликов, остаются вспышка, трещины и волна
 * @param {function(): number} [params.rng]
 */
export const buildBuildingImpactCastPlan = ({center, hexSize, kind, palette, isReduced = false, rng = Math.random}) => {
  const impact = BUILDING_IMPACTS[kind];
  const {flash, glow, scorch, cracks, rings, sparks, shards, rays, pillar, motes, seal, shake} = impact;
  const size = hexSize;
  const elements = [];

  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.scorch, sizePx: size * scorch.sizeFactor,
    durationMs: scorch.durationMs, layer: 'ground', order: 0,
    pose: (local) => ({
      x: center.x,
      y: center.y,
      scaleX: 1,
      scaleY: GROUND_SQUASH,
      alpha: scorch.alpha * fadeOutFrom(local, 0.4),
    }),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: palette.accent, sizePx: size * glow.sizeFactor,
    durationMs: glow.durationMs, layer: 'ground', order: 1,
    pose: (local) => ({
      x: center.x,
      y: center.y,
      scaleX: lerp(0.7, 1, easeOutCubic(local)),
      scaleY: lerp(0.7, 1, easeOutCubic(local)) * GROUND_SQUASH,
      alpha: glow.alpha * (1 - local) ** 1.3,
    }),
  }));

  // Горячий ореол вокруг жетона: лежит над пылью, поэтому удар виден и сквозь нее
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: palette.accent, sizePx: size * glow.sizeFactor * 0.62,
    durationMs: glow.durationMs * 0.7, order: 5,
    pose: (local) => ({
      x: center.x,
      y: center.y,
      scale: lerp(0.8, 1.15, easeOutCubic(local)),
      alpha: glow.alpha * (1 - local) ** 1.6,
    }),
  }));

  // Трещины раскалены, а потом остывают и гаснут
  if (cracks) {
    const crackRotation = rng() * TWO_PI;
    // Темный разлом под раскаленной линией: без него на светлой подложке трещин не видно
    elements.push(createElement({
      textureKey: BUILDING_IMPACT_TEXTURE_KEYS.CRACKS, tint: COLORS.scorch, sizePx: size * cracks.sizeFactor * 1.015,
      durationMs: cracks.durationMs * 1.25, layer: 'ground', order: 2,
      pose: (local) => {
        const grow = easeOutCubic(clamp01(local * 8.75));
        return {
          x: center.x,
          y: center.y,
          scaleX: lerp(0.55, 1, grow),
          scaleY: lerp(0.55, 1, grow) * GROUND_SQUASH,
          rotation: crackRotation,
          alpha: 0.9 * fadeOutFrom(local, 0.5),
        };
      },
    }));
    elements.push(createElement({
      textureKey: BUILDING_IMPACT_TEXTURE_KEYS.CRACKS, tint: COLORS.crackHot, sizePx: size * cracks.sizeFactor,
      durationMs: cracks.durationMs, layer: 'ground', order: 3,
      pose: (local) => {
        const grow = easeOutCubic(clamp01(local * 7));
        return {
          x: center.x,
          y: center.y,
          scaleX: lerp(0.55, 1, grow),
          scaleY: lerp(0.55, 1, grow) * GROUND_SQUASH,
          rotation: crackRotation,
          alpha: fadeOutFrom(local, 0.45),
          tint: local < 0.3
            ? mixColor(COLORS.crackHot, COLORS.crackWarm, local / 0.3)
            : mixColor(COLORS.crackWarm, COLORS.crackCold, (local - 0.3) / 0.7),
        };
      },
    }));
  }

  pushRings(elements, {center, size, rings, palette});

  if (!isReduced) {
    if (pillar) {
      pushPillar(elements, {center, size, pillar, palette});
    }
    if (rays) {
      pushRays(elements, {center, size, rays, palette, rng});
    }
    if (seal) {
      pushSeal(elements, {center, size, seal, palette});
    }
    pushShards(elements, {center, size, shards, rng});
    pushSparks(elements, {center, size, sparks, palette, rng});
    if (motes) {
      pushMotes(elements, {center, size, motes, palette, rng});
    }
  }

  // Вспышка: мягкий диск и звезда из-за жетона
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: palette.light, sizePx: size * flash.sizeFactor,
    durationMs: flash.durationMs, order: 50,
    pose: (local) => ({
      x: center.x,
      y: center.y,
      scale: lerp(0.55, 1.1, easeOutCubic(local)),
      alpha: flash.alpha * (1 - local),
    }),
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.white, sizePx: size * flash.sizeFactor * 0.7,
    durationMs: flash.durationMs * 0.8, order: 51,
    pose: (local) => ({
      x: center.x,
      y: center.y,
      scale: lerp(0.5, 1, easeOutCubic(local)),
      alpha: flash.alpha * (1 - local) ** 1.5,
    }),
  }));

  return toPlan(elements, {shake});
};
